import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Home from '../containers/home/Home';
import InternalLogin from '../containers/auth/InternalLogin';
import LoginCallback from '../containers/auth/LoginCallback';
import AuthRoute from './AuthRoute';
import PrivateRoute from './PrivateRoute';
import NoAccess from '../containers/home/NoAccess';
import MemberLogin from '../containers/auth/member/MemberLogin';
import MemberCreateAccount from '../containers/auth/member/MemberCreateAccount';
import MemberVerifyEmail from '../containers/auth/member/MemberVerifyEmail';
import MemberVerifyAccount from '../containers/auth/member/MemberVerifyAccount';
import MemberPrivateRoute from './MemberPrivateRoute';
import { usePortalName } from '../hooks/usePortalName';
import MemberResetPassword from '../containers/auth/member/MemberResetPassword';
import MemberHome from '../containers/member/MemberHome';
import authService from '../services/authService';
import { useNeedsAccountVerification } from '../hooks/useNeedsAccountVerification';
import { useNeedsEmailVerification } from '../hooks/useNeedsEmailVerification';
import { useUserCategoryTypeBC } from '../hooks/useUserCategoryTypeBC';
import ClientLogin from '../containers/auth/client/ClientLogin';
import * as userSlice from '../data/user.slice';
import { useAppSelector } from '../hooks/useAppSelector';
import { Box, CircularProgress, styled } from '@mui/material';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useCognitoPopupLogin } from '../hooks/useCognitoPopupLogin';
import { useAwsConnectWorkspace } from '../hooks/useAwsConnectWorkspace';
import { usePortalClientBC } from '../hooks/usePortalClientBC';
import { useUserClientBC } from '../hooks/useUserClientBC';
import { UserCategoryTypeBC } from '../data/models/user-category-type.model';

const PREFIX = 'AppRouting';

const classes = {
  container: `${PREFIX}-container`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    backgroundColor: 'white',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const AppRouting = () => {
  const dispatch = useAppDispatch();
  const portalName = usePortalName();
  const needsEmailVerification = useNeedsEmailVerification();
  const needsAccountVerification = useNeedsAccountVerification();
  const userCategoryTypeBC = useUserCategoryTypeBC();
  const isLoggedIn = authService.isLoggedIn();
  const initialized = useAppSelector(userSlice.selectLoggedInUserInitialized);
  const userClientBC = useUserClientBC();
  const federatedProvider = authService.getFederatedCustomProvider(portalName);

  // Picks up logins completed in the popup flow (iframe / Connect Workspace).
  useCognitoPopupLogin();

  // Turns an incoming call in the Amazon Connect workspace into a passenger search. Inert in a
  // plain browser and on the client portals.
  useAwsConnectWorkspace(isLoggedIn);

  useEffect(() => {
    dispatch(userSlice.fetchLoggedInUserInfoAsync());
  }, []);

  if (!initialized) {
    return (
      <Root className={classes.container}>
        <Box>
          <CircularProgress color="primary" size={120} />
        </Box>
      </Root>
    );
  }

  if (portalName) {
    if (needsEmailVerification) {
      return (
        <Routes>
          <Route path="/memberVerifyEmail" element={<MemberVerifyEmail />} />
          <Route
            path="*"
            element={<Navigate to={'/memberVerifyEmail'} replace />}
          />
        </Routes>
      );
    }

    if (
      needsAccountVerification &&
      (userCategoryTypeBC === undefined || // this is the case if they did a federated login we assume they are on the right portal
        userCategoryTypeBC === UserCategoryTypeBC.Passenger) // this is checking username/password to make sure they are on the same portal the account was created on
    ) {
      return (
        <Routes>
          <Route
            path="/memberVerifyAccount"
            element={<MemberVerifyAccount />}
          />
          <Route
            path="*"
            element={<Navigate to={'/memberVerifyAccount'} replace />}
          />
        </Routes>
      );
    }

    const portalClientBC = usePortalClientBC(portalName);
    if (isLoggedIn && userClientBC !== portalClientBC) {
      return (
        <Routes>
          <Route path="/noAccess" element={<NoAccess />} />
          <Route
            path="/login"
            element={
              <AuthRoute>
                <MemberLogin />
              </AuthRoute>
            }
          />
          <Route
            path="/clientLogin"
            element={
              <AuthRoute>
                <ClientLogin />
              </AuthRoute>
            }
          />
          <Route path="*" element={<Navigate to={'/noAccess'} replace />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/loginCallback" element={<LoginCallback />} />
        <Route
          path="/login"
          element={
            <AuthRoute>
              <MemberLogin />
            </AuthRoute>
          }
        />
        <Route
          path="/clientLogin"
          element={
            <AuthRoute>
              <ClientLogin />
            </AuthRoute>
          }
        />
        <Route
          path="/memberCreateAccount"
          element={
            <AuthRoute>
              <MemberCreateAccount />
            </AuthRoute>
          }
        />
        <Route
          path="/memberResetPassword"
          element={
            <AuthRoute>
              <MemberResetPassword />
            </AuthRoute>
          }
        />
        <Route path="/memberVerifyEmail" element={<MemberVerifyEmail />} />

        <Route path="/noAccess" element={<NoAccess />} />

        <Route
          path="*"
          element={
            <MemberPrivateRoute>
              <MemberHome />
            </MemberPrivateRoute>
          }
        />
      </Routes>
    );
  } else {
    return (
      <Routes>
        <Route path="/loginCallback" element={<LoginCallback />} />
        <Route
          path="/internallogin"
          element={
            <AuthRoute>
              <InternalLogin />
            </AuthRoute>
          }
        />

        <Route path="/noAccess" element={<NoAccess />} />

        <Route
          path="*"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
      </Routes>
    );
  }
};

export default AppRouting;
